import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';

type UnitRow = { id: string; code?: string; name?: string };

/**
 * Curriculum unit picker (optional link). Reads the existing curriculum graph; a user without
 * education.curriculum.view simply sees an empty list and can leave the field blank.
 */
export default function LmsUnitSelect({ subjectId, ...props }: Omit<SelectProps, 'options'> & { subjectId?: string }) {
  const { t } = useTranslation('education');
  const query = useQuery({
    queryKey: ['education-lms-units', subjectId],
    enabled: Boolean(subjectId),
    queryFn: async () => {
      try {
        const res = await api.get<UnitRow[]>('/education/curriculum/units', {
          params: { subject_id: subjectId, active_only: true },
        });
        return Array.isArray(res.data) ? res.data : [];
      } catch {
        return [] as UnitRow[];
      }
    },
  });
  return (
    <Select
      allowClear
      showSearch
      optionFilterProp="label"
      placeholder={t('lmsCurriculumUnit', { defaultValue: 'Curriculum unit (optional)' })}
      loading={query.isLoading}
      disabled={!subjectId}
      options={(query.data ?? []).map((row) => ({
        value: row.id,
        label: `${row.code || row.id.slice(0, 8)} — ${row.name || ''}`,
      }))}
      {...props}
    />
  );
}
